/**
 * Higher-timeframe projection — a stepped series from a coarser timeframe drawn
 * on an intraday chart.
 *
 * Deliberately does NOT participate in the price axis. Like `previousClose`, it
 * registers with `figures: []`, so klinecharts' `calcRange` — which only folds in
 * declared figure values — never sees it. The consequence is intentional: when the
 * projected value sits outside the visible range the line simply is not drawn,
 * rather than stretching the axis and squashing the intraday detail.
 *
 * The FIELD set is closed and computed here. That is what keeps this from being an
 * indicator that references other indicators: there is no dependency graph to
 * resolve and no way to build a cycle. Letting a user project an arbitrary
 * indicator wants a `timeframe` param on that indicator instead, so its own `calc`
 * runs over aggregated bars.
 *
 * Targets are INTRADAY only. `aggregateBars`-style clock flooring is correct for
 * minute and hour buckets, but a daily bucket would start at UTC midnight
 * (19:00/20:00 in New York) rather than at the session open. This module still
 * does not bucket by session: daily and weekly INDICATOR timeframes map a
 * fetched daily series by session date instead (mtfSession, MTF Phase B).
 */

/** Price fields projectable from the coarser timeframe. */
export type HtfField = 'close' | 'open' | 'high' | 'low'

/** The only bar shape this needs. */
export interface HtfBar {
  timestamp: number
  open: number
  high: number
  low: number
  close: number
}

/**
 * One coarser-timeframe value.
 *
 * A single object is SHARED by every chart bar inside its bucket, so the renderer
 * finds bucket runs with reference equality (`a === b`) instead of re-deriving
 * boundaries — the trick `drawPivotSegments` uses for daily pivot levels.
 */
export interface HtfPoint {
  /** The projected field value. */
  value: number
  /** Bucket start, for debugging and tests. */
  bucketStart: number
  /** True while this bucket is still the last one in the data, i.e. forming. */
  forming: boolean
}

/**
 * One coarser-timeframe BAR — the full OHLC of the bucket.
 *
 * Shared by reference across the bucket's chart bars, exactly like HtfPoint, so
 * the renderer groups by identity rather than re-deriving boundaries.
 */
export interface HtfBucket {
  open: number
  high: number
  low: number
  close: number
  bucketStart: number
  /** True while this is the last bucket in the data, i.e. still forming. */
  forming: boolean
}

/**
 * Project the coarser timeframe's full bars onto each chart bar.
 *
 * The OHLC form is what a shaded-candle render needs: body from open to close,
 * wick from low to high. `projectHtfField` is the single-field view of the same
 * bucketing, so the two can never disagree about where a bucket starts.
 */
export function projectHtfBuckets(
  bars: readonly HtfBar[],
  targetMs: number,
  chartMs: number,
): Array<HtfBucket | undefined> {
  const out: Array<HtfBucket | undefined> = new Array(bars.length).fill(undefined)
  if (bars.length === 0) return out
  if (!Number.isFinite(targetMs) || targetMs <= 0) return out
  // A target at or below the chart's own interval is not a HIGHER timeframe.
  if (!Number.isFinite(chartMs) || chartMs <= 0 || targetMs <= chartMs) return out

  let i = 0
  while (i < bars.length) {
    const bucketStart = Math.floor(bars[i].timestamp / targetMs) * targetMs
    let j = i
    const open = bars[i].open
    let high = -Infinity
    let low = Infinity
    let close = bars[i].close
    while (j < bars.length && Math.floor(bars[j].timestamp / targetMs) * targetMs === bucketStart) {
      const b = bars[j]
      if (b.high > high) high = b.high
      if (b.low < low) low = b.low
      close = b.close
      j++
    }
    if (Number.isFinite(open) && Number.isFinite(high) && Number.isFinite(low)) {
      const bucket: HtfBucket = {
        open,
        high,
        low,
        close,
        bucketStart,
        forming: j >= bars.length,
      }
      for (let k = i; k < j; k++) out[k] = bucket
    }
    i = j
  }
  return out
}

/**
 * Project a coarser-timeframe field onto each chart bar.
 *
 * Returns one entry per input bar — the value of the bucket that bar belongs to —
 * or `undefined` where nothing applies. Bars are assumed ascending by timestamp.
 *
 * `targetMs` must exceed the chart's own bar interval; a target at or below it
 * describes the bars already on screen, so the result is all `undefined` rather
 * than a line duplicating the price series.
 */
export function projectHtfField(
  bars: readonly HtfBar[],
  targetMs: number,
  field: HtfField,
  chartMs: number,
): Array<HtfPoint | undefined> {
  // Derived from projectHtfBuckets rather than re-bucketing, so the field view
  // and the OHLC view can never disagree about bucket boundaries.
  const buckets = projectHtfBuckets(bars, targetMs, chartMs)
  const out: Array<HtfPoint | undefined> = new Array(bars.length).fill(undefined)
  // One HtfPoint per distinct bucket, keeping the shared-reference property.
  const seen = new Map<HtfBucket, HtfPoint>()
  for (let i = 0; i < buckets.length; i++) {
    const b = buckets[i]
    if (!b) continue
    let point = seen.get(b)
    if (!point) {
      const value =
        field === 'open' ? b.open : field === 'high' ? b.high : field === 'low' ? b.low : b.close
      if (!Number.isFinite(value)) continue
      point = { value, bucketStart: b.bucketStart, forming: b.forming }
      seen.set(b, point)
    }
    out[i] = point
  }
  return out
}

/** Contiguous index runs sharing one projected object, over `[from, to)`. */
export interface HtfRun<T = HtfPoint> {
  startIdx: number
  endIdx: number
  point: T
}

/**
 * Group projected values into runs by REFERENCE, so each coarser bar becomes one
 * flat segment. Comparing by reference rather than by number matters: two adjacent
 * buckets can close at exactly the same price, and drawing them as a single
 * segment would hide the boundary between them.
 */
export function htfRuns<T>(
  points: ReadonlyArray<T | undefined>,
  from: number,
  to: number,
): Array<HtfRun<T>> {
  const runs: Array<HtfRun<T>> = []
  let start = -1
  let current: T | undefined
  const lo = Math.max(0, from)
  const hi = Math.min(points.length, to)

  for (let i = lo; i < hi; i++) {
    const p = points[i]
    if (p !== current) {
      if (current) runs.push({ startIdx: start, endIdx: i - 1, point: current })
      current = p
      start = i
    }
  }
  if (current && start >= 0) runs.push({ startIdx: start, endIdx: hi - 1, point: current })
  return runs
}
